import React, { createContext, useContext, useState, useEffect } from 'react';

const PREDEFINED_THEMES = {
  default: { name: 'Default (White)', description: 'Clean and professional', preview: ['#4f46e5', '#1e293b', '#f8fafc'], background: '#ffffff', sidebar: '#1e293b', sidebarText: '#e2e8f0', accent: '#4f46e5', headerBg: '#f8fafc', cardBg: '#ffffff', textPrimary: '#1e293b', textSecondary: '#64748b' },
  slate: { name: 'Slate', description: 'Cool and modern', preview: ['#3b82f6', '#0f172a', '#e2e8f0'], background: '#f1f5f9', sidebar: '#0f172a', sidebarText: '#cbd5e1', accent: '#3b82f6', headerBg: '#e2e8f0', cardBg: '#ffffff', textPrimary: '#0f172a', textSecondary: '#475569' },
  warm: { name: 'Warm Sand', description: 'Earthy and inviting', preview: ['#d97706', '#44403c', '#f5f0e8'], background: '#faf7f2', sidebar: '#44403c', sidebarText: '#e7e5e4', accent: '#d97706', headerBg: '#f5f0e8', cardBg: '#ffffff', textPrimary: '#292524', textSecondary: '#78716c' },
  ocean: { name: 'Ocean Blue', description: 'Calm and focused', preview: ['#2563eb', '#1e3a5f', '#dbeafe'], background: '#f0f7ff', sidebar: '#1e3a5f', sidebarText: '#bfdbfe', accent: '#2563eb', headerBg: '#dbeafe', cardBg: '#ffffff', textPrimary: '#1e3a5f', textSecondary: '#3b82f6' },
  forest: { name: 'Forest Green', description: 'Natural and grounded', preview: ['#16a34a', '#14532d', '#dcfce7'], background: '#f0fdf4', sidebar: '#14532d', sidebarText: '#bbf7d0', accent: '#16a34a', headerBg: '#dcfce7', cardBg: '#ffffff', textPrimary: '#14532d', textSecondary: '#15803d' },
  dark: { name: 'Dark Mode', description: 'Easy on the eyes', preview: ['#6366f1', '#020617', '#1e293b'], background: '#0f172a', sidebar: '#020617', sidebarText: '#94a3b8', accent: '#6366f1', headerBg: '#1e293b', cardBg: '#1e293b', textPrimary: '#e2e8f0', textSecondary: '#94a3b8' },
  custom: { name: 'Custom', description: 'Make it yours', preview: ['#4f46e5', '#1e293b', '#f8fafc'], background: '#ffffff', sidebar: '#1e293b', sidebarText: '#e2e8f0', accent: '#4f46e5', headerBg: '#f8fafc', cardBg: '#ffffff', textPrimary: '#1e293b', textSecondary: '#64748b' },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => localStorage.getItem('eyvo_theme') || 'default');
  const [customColors, setCustomColors] = useState(() => {
    try { return JSON.parse(localStorage.getItem('eyvo_custom_theme') || '{}'); } catch { return {}; }
  });

  const currentTheme = themeId === 'custom'
    ? { ...PREDEFINED_THEMES.default, ...customColors }
    : PREDEFINED_THEMES[themeId] || PREDEFINED_THEMES.default;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--eyvo-bg', currentTheme.background);
    root.style.setProperty('--eyvo-sidebar', currentTheme.sidebar);
    root.style.setProperty('--eyvo-sidebar-text', currentTheme.sidebarText);
    root.style.setProperty('--eyvo-accent', currentTheme.accent);
    root.style.setProperty('--eyvo-header-bg', currentTheme.headerBg);
    root.style.setProperty('--eyvo-card-bg', currentTheme.cardBg);
    root.style.setProperty('--eyvo-text-primary', currentTheme.textPrimary);
    root.style.setProperty('--eyvo-text-secondary', currentTheme.textSecondary);
    document.body.style.backgroundColor = currentTheme.background;

    // Also override the shadcn bg-background class
    const mainContent = document.getElementById('main-content');
    if (mainContent) mainContent.style.backgroundColor = currentTheme.background;
  }, [currentTheme]);

  const setTheme = (id) => { setThemeId(id); localStorage.setItem('eyvo_theme', id); };

  const updateCustomColor = (key, value) => {
    const updated = { ...customColors, [key]: value };
    setCustomColors(updated);
    localStorage.setItem('eyvo_custom_theme', JSON.stringify(updated));
  };

  return (
    <ThemeContext.Provider value={{ themeId, currentTheme, setTheme, updateCustomColor, customColors, themes: PREDEFINED_THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() { return useContext(ThemeContext); }
export { PREDEFINED_THEMES };
export default ThemeContext;
